import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useProfileData } from "../../../hooks/useProfileData";
import { getAudienceByLocation, getFollowersCountPerAge } from "../../services/entities";
import { GenderAgeGroupData } from "../../../types";

type AudienceByLocation = {
  location: string;
  countryName: string;
  count: number;
};

type AudienceByLocationResponse = { data: { result: AudienceByLocation[] }; message: string };

type FollowersPerAgeResponse = { data: GenderAgeGroupData };

type UseAudienceDataResult = {
  audienceByLocationData: AudienceByLocationResponse | undefined;
  isAudienceByLocationDataLoading: boolean;
  followersPerAgeData: FollowersPerAgeResponse | undefined;
  isFollowersPerAgeDataLoading: boolean;
};

export const useQueryAudienceData = (location: "CITY" | "COUNTRY"): UseAudienceDataResult => {
  const { i18n } = useTranslation();
  const { data: userData, isLoading: isUserDataLoading } = useProfileData();
  const entityId = userData?.entityId;

  const { data: audienceByLocationData, isLoading: isAudienceByLocationDataLoading } =
    useQuery<AudienceByLocationResponse>({
      queryKey: ["Dashboard", "AudienceLocation", i18n.language, entityId, location],
      queryFn: async () => await getAudienceByLocation(entityId, location),
      enabled: !!entityId,
    });

  const { data: followersPerAgeData, isLoading: isFollowersPerAgeDataLoading } = useQuery<FollowersPerAgeResponse>({
    queryKey: ["Dashboard", "Audience", i18n.language, entityId],
    queryFn: async () => await getFollowersCountPerAge(entityId),
    enabled: !!entityId,
  });

  return {
    audienceByLocationData,
    isAudienceByLocationDataLoading: isAudienceByLocationDataLoading || isUserDataLoading,
    followersPerAgeData,
    isFollowersPerAgeDataLoading: isFollowersPerAgeDataLoading || isUserDataLoading,
  };
};
